import type {
  AgentPriority,
  CustomAgentResultItem,
  PMComponentRef
} from '@axilon-apps/domains-ontology-ui-state'

import type { InsightImpact } from '../insights'

export const RCA_CONSUMED_KEYS = [
  'headline',
  'one_line_summary',
  'event_window',
  'impact',
  'root_causes',
  'impacts',
  'action_items'
] as const

export const PM_CONSUMED_KEYS = ['predictions', 'action_items', 'healthy'] as const

export const SUMMARY_CONSUMED_KEYS = [
  'headline',
  'narrative',
  'metrics',
  'notable_events',
  'action_items'
] as const

export interface MappedRootCause {
  title: string
  reasoning: string
  confidence: number
  components: PMComponentRef[]
}

export interface MappedActionItem {
  title: string
  description: string
  priority: AgentPriority
  components: PMComponentRef[]
}

export interface MappedImpactRow {
  process: string
  upstream: boolean
  confidence: number
  reasoning: string
}

export interface MappedEventWindow {
  start?: string
  end?: string
}

export interface MappedPrediction {
  equipment: string
  failureMode: string
  chance: number
  reasoning: string
  recommendedAction?: string
  eventWindow: MappedEventWindow
  impact?: InsightImpact
  components: PMComponentRef[]
}

export interface MappedSummaryMetric {
  label: string
  value: number
  unit?: string
  deltaVsPriorPeriod?: number
}

export type MappedSummaryEventSeverity = 'info' | 'warning' | 'critical'

export interface MappedSummaryEvent {
  occurredAt?: string
  description: string
  severity?: MappedSummaryEventSeverity
  evidence: PMComponentRef[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function asPriority(value: unknown): AgentPriority {
  if (value === 'critical' || value === 'high' || value === 'medium' || value === 'low') {
    return value
  }
  return 'medium'
}

/**
 * Tag paths as non-linkable chips. Do not fabricate graph node ids — a raw
 * path is not a plant URN and must not deep-link until resolution lands.
 */
export function tagPathsToUnlinkedChips(paths: unknown): PMComponentRef[] {
  if (!Array.isArray(paths)) return []
  const out: PMComponentRef[] = []
  for (const path of paths) {
    if (typeof path !== 'string' || !path.trim()) continue
    const id = path.trim()
    out.push({ scope: 'tag', id, name: id, linkable: false })
  }
  return out
}

export function leftoverRecord(
  result: Record<string, unknown>,
  consumed: readonly string[]
): Record<string, unknown> | null {
  const skip = new Set(consumed)
  const leftover: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(result)) {
    if (!skip.has(key)) leftover[key] = value
  }
  return Object.keys(leftover).length > 0 ? leftover : null
}

export function mapEventWindow(raw: unknown): MappedEventWindow {
  if (!isRecord(raw)) return {}
  return { start: asString(raw.start), end: asString(raw.end) }
}

export function mapImpact(raw: unknown): InsightImpact | undefined {
  if (!isRecord(raw)) return undefined
  const context = asString(raw.context) ?? ''
  const value = asNumber(raw.value) ?? null
  const unit = asString(raw.unit) ?? null
  if (!context && value == null && !unit) return undefined
  return { context, value, unit }
}

export function mapRootCauses(raw: unknown): MappedRootCause[] {
  if (!Array.isArray(raw)) return []
  const out: MappedRootCause[] = []
  for (const [index, item] of raw.entries()) {
    if (!isRecord(item)) continue
    out.push({
      title: asString(item.title) ?? `Root cause ${index + 1}`,
      reasoning: asString(item.reasoning) ?? '',
      confidence: asNumber(item.confidence) ?? 0,
      components: tagPathsToUnlinkedChips(item.evidence_tag_paths)
    })
  }
  return out
}

export function mapActionItems(raw: unknown): MappedActionItem[] {
  if (!Array.isArray(raw)) return []
  const out: MappedActionItem[] = []
  for (const item of raw) {
    if (!isRecord(item)) continue
    out.push({
      title: asString(item.title) ?? 'Untitled action',
      description: asString(item.description) ?? '',
      priority: asPriority(item.priority),
      components: tagPathsToUnlinkedChips(item.evidence_tag_paths)
    })
  }
  return out
}

export function mapImpactRows(raw: unknown): MappedImpactRow[] {
  if (!Array.isArray(raw)) return []
  const out: MappedImpactRow[] = []
  for (const item of raw) {
    if (!isRecord(item)) continue
    const process = asString(item.process)
    if (!process) continue
    out.push({
      process,
      upstream: item.is_upstream_of_root_cause === true,
      confidence: asNumber(item.confidence) ?? 0,
      reasoning: asString(item.reasoning) ?? ''
    })
  }
  return out
}

export function mapPredictions(raw: unknown): MappedPrediction[] {
  if (!Array.isArray(raw)) return []
  const out: MappedPrediction[] = []
  for (const item of raw) {
    if (!isRecord(item)) continue
    const failureMode = asString(item.failure_mode)
    if (!failureMode) continue
    out.push({
      equipment: asString(item.equipment) ?? '',
      failureMode,
      chance: asNumber(item.chance) ?? 0,
      reasoning: asString(item.reasoning) ?? '',
      recommendedAction: asString(item.recommended_action),
      eventWindow: mapEventWindow(item.event_window),
      impact: mapImpact(item.impact),
      components: tagPathsToUnlinkedChips(item.evidence_tag_paths)
    })
  }
  return out
}

export function mapSummary(raw: unknown): string | undefined {
  return asString(raw)
}

export function mapSummaryMetrics(raw: unknown): MappedSummaryMetric[] {
  if (!Array.isArray(raw)) return []
  const out: MappedSummaryMetric[] = []
  for (const row of raw) {
    if (!isRecord(row)) continue
    const label = asString(row.label)
    const value = asNumber(row.value)
    if (!label || value === undefined) continue
    out.push({
      label,
      value,
      unit: asString(row.unit),
      deltaVsPriorPeriod: asNumber(row.delta_vs_prior_period)
    })
  }
  return out
}

/** One notable event off the wire. null when it carries no description. */
export function mapSummaryNotableEvent(raw: unknown): MappedSummaryEvent | null {
  if (!isRecord(raw)) return null
  const description = asString(raw.description)
  if (!description) return null
  const severity = raw.severity
  return {
    occurredAt: asString(raw.occurred_at),
    description,
    severity:
      severity === 'warning' || severity === 'critical' || severity === 'info'
        ? severity
        : undefined,
    evidence: tagPathsToUnlinkedChips(raw.evidence_tag_paths)
  }
}

export function mapSummaryNotableEvents(raw: unknown): MappedSummaryEvent[] {
  if (!Array.isArray(raw)) return []
  const out: MappedSummaryEvent[] = []
  for (const row of raw) {
    const event = mapSummaryNotableEvent(row)
    if (event) out.push(event)
  }
  return out
}

/** A shredded notable-event row enriched with its correction state. */
export interface MappedCorrectableSummaryEvent extends MappedSummaryEvent {
  /** `custom_agent_results` row id — the target of the correction PATCH. */
  id: string
  approval_status: CustomAgentResultItem['approval_status']
  denialNotes?: string
}

/**
 * One shredded notable-event row with its review state. The proxy shreds
 * summary events as approved (`x-reviewable-default-value` on the template
 * schema), so the row's approval_status is authoritative — no client-side
 * defaulting.
 */
export function mapSummaryNotableEventFromItem(
  item: CustomAgentResultItem
): MappedCorrectableSummaryEvent | null {
  const parsed = mapSummaryNotableEvent(item.result)
  if (!parsed) return null
  return {
    ...parsed,
    id: item.id,
    approval_status: item.approval_status,
    ...(item.feedback ? { denialNotes: item.feedback } : {})
  }
}
